/**
 * Polling that respects tab visibility (standards/resource-respect.md#polling):
 * nothing runs while the tab is hidden, and one check runs as soon as it is
 * visible again, so the user never sees a stale state for a full interval.
 */
export interface VisibilityTarget {
  readonly hidden: boolean;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/** Calls `cb` each time the tab goes from hidden to visible. Returns an unsubscribe. */
export function onBecameVisible(cb: () => void, doc: VisibilityTarget = document): () => void {
  const listener = () => { if (!doc.hidden) cb(); };
  doc.addEventListener('visibilitychange', listener);
  return () => doc.removeEventListener('visibilitychange', listener);
}

/** Runs `tick` every `intervalMs` while the tab is visible. Returns a stop function. */
export function startVisibleInterval(
  tick: () => void,
  intervalMs: number,
  doc: VisibilityTarget = document,
): () => void {
  let timer: ReturnType<typeof setInterval> | null = null;
  const start = () => { if (timer === null) timer = setInterval(tick, intervalMs); };
  const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
  const listener = () => {
    if (doc.hidden) {
      stop();
    } else {
      tick();
      start();
    }
  };
  if (!doc.hidden) start();
  doc.addEventListener('visibilitychange', listener);
  return () => {
    stop();
    doc.removeEventListener('visibilitychange', listener);
  };
}
